import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';

import { FIREBASE_AUTH_ERROR_CODES } from '@src/constants/commons';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import User from '@src/models/User';
import type { ElasticQueryParams } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type { ElasticSearchResponse } from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import type {
  TUserOrganization,
  UserNotificationTargetDTO,
} from '@src/models/interfaces/user.interface';
import type {
  IChangePasswordParams,
  ICloudFunctionResult,
  IUpdateProfileData,
} from '@src/models/interfaces/user-form.interface';
import type { ISendUserInvitationParams, ISendUserUpdateParams } from '@src/models/interfaces/requests/user';
import type { UserListSearchField } from '@src/models/mappers/UserMapper';
import { getDocData } from '@src/packages/utils';
import { chunkIds, FIRESTORE_IN_LIMIT } from '@src/packages/utils/list';
import type { JurisdictionIdentifier } from '@src/packages/utils/nationalId';
import type {
  TrackingMode,
  TrackingPrecision,
} from '@src/packages/users/utils/trackingPolicy';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import safeExecute from '@src/util/safeExecute';

interface IGetUsersWithJourneyParams {
  organizationId: string;
  pageSize?: number;
  pageToken?: string | null;
  workShiftId?: string;
  workJourneyConfigId?: string;
  searchTerm?: string;
  searchFields?: UserListSearchField[];
}

interface IGetUsersWithJourneyResult {
  users?: unknown[];
  nextPageToken?: string | null;
  total?: number;
}

class UserRepository {
  private usersCollection: string;
  private organizationCollection: string;
  private user: User;
  private updatePassword: typeof updatePassword;
  private authUser: typeof getAuth;
  private reauthenticateWithCredential: typeof reauthenticateWithCredential;

  constructor() {
    this.usersCollection = FSCollections.USERS;
    this.organizationCollection = FSCollections.ORGANIZATIONS;
    this.user = new User();
    this.updatePassword = updatePassword;
    this.authUser = getAuth;
    this.reauthenticateWithCredential = reauthenticateWithCredential;
  }

  getUsersPath(orgId: string): string {
    return `${this.organizationCollection}/${orgId}/${this.usersCollection}`;
  }

  /**
   * Sends user invitation to Cloud Function
   *
   * ⭐ Repository Pattern: Data Access Only
   * - Receives already-structured params from Service layer
   * - Makes API call without any data manipulation
   * - Returns raw API response without mapping
   *
   * Expected endpoint signature:
   * POST /sendUserInvitation
   * {
   *   organizationId: string;
   *   userCreationParams: ITUserCreationParams;
   * }
   *
   * @param params ISendUserInvitationParams with organizationId + userCreationParams
   * @returns Raw API response from Cloud Function
   * @throws API errors as-is for Service layer to handle
   */
  async create(params: ISendUserInvitationParams): Promise<ICloudFunctionResult> {
    const sendUserInvitationFn = apiCallable<
      ISendUserInvitationParams,
      ICloudFunctionResult
    >(API_OPERATIONS.USER_INVITATION);

    const { data: result } = await sendUserInvitationFn(params);
    return result;
  }

  /**
   * Updates existing user via Cloud Function
   *
   * ⭐ Repository Pattern: Data Access Only
   * - Receives already-structured params from Service layer
   * - Makes API call without any data manipulation
   * - Returns raw API response without mapping
   *
   * Expected endpoint signature:
   * POST /updateUser
   * {
   *   organizationId: string;
   *   userUpdateParams: ITUserUpdateParams;
   * }
   *
   * @param params ISendUserUpdateParams with organizationId + userUpdateParams
   * @returns Raw API response from Cloud Function
   * @throws API errors as-is for Service layer to handle
   */
  async update(params: ISendUserUpdateParams): Promise<ICloudFunctionResult> {
    const updateUserFn = apiCallable<
      ISendUserUpdateParams,
      ICloudFunctionResult
    >(API_OPERATIONS.UPDATE_USER);

    const { data: result } = await updateUserFn(params);
    return result;
  }

  /**
   * Patches only the org-scoped attendance jurisdiction overlay.
   * Avoids `user.update`, which requires full membership fields (divisions, etc.).
   * Firestore rules allow this only for org Owner/Admin — division editors on
   * WORK_JOURNEY flows get permission-denied (same as the former `user.update` path).
   *
   * This path only sets `jurisdictionId` (never clears it). Clearing would require
   * `FieldValue.delete()` and a nullable API; callers always pass a built identifier.
   */
  async updateJurisdictionId(
    organizationId: string,
    userId: string,
    jurisdictionId: JurisdictionIdentifier,
  ): Promise<void> {
    return safeExecute(async () => {
      const userRef = doc(
        db,
        this.organizationCollection,
        organizationId,
        this.usersCollection,
        userId,
      );
      await updateDoc(userRef, { jurisdictionId });
    });
  }

  /**
   * Persists tracking policy on the organization user document (Owner/Admin rules).
   * Same path as UserRealTimeTracking — used when edit must not rely only on user.update.
   */
  async updateTrackingPolicy(
    organizationId: string,
    userId: string,
    tracking: {
      trackingMode: TrackingMode;
      trackingPrecision: TrackingPrecision;
      isTracked: boolean;
    },
  ): Promise<void> {
    return safeExecute(async () => {
      const userRef = doc(
        db,
        this.organizationCollection,
        organizationId,
        this.usersCollection,
        userId,
      );
      await setDoc(
        userRef,
        {
          ...tracking,
          updatedAt: new Date(),
        },
        { merge: true },
      );
    });
  }

  async updateProfile(data: IUpdateProfileData): Promise<ReturnType<User['updateProfile']>> {
    return safeExecute(async () => {
      this.user.setData(data);
      const user = this.user.updateProfile();
      const updateUserProfileFn = apiCallable<
        Record<string, unknown>,
        ICloudFunctionResult
      >(API_OPERATIONS.UPDATE_USER_PROFILE);

      const { data: result } = await updateUserProfileFn({
        uid: data.id,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        locale: user.locale,
        timeZone: user.timeZone,
        idCard: user.idCard,
        birthDate: user.birthDate,
        ...(user.address !== undefined ? { address: user.address } : {}),
      });

      if (!result?.success) {
        throw new Error(
          typeof (result as { error?: string })?.error === 'string'
            ? (result as { error: string }).error
            : 'Failed to update profile',
        );
      }

      return user;
    }).catch((error: Error) => {
      throw error;
    });
  }

  async updateProfilePassword({
    currentPassword,
    password,
  }: IChangePasswordParams): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const auth = await this.authUser();
      const credential = EmailAuthProvider.credential(auth.currentUser!.email!, currentPassword);

      return this.reauthenticateWithCredential(auth.currentUser!, credential)
        .then(async authResult => {
          try {
            await this.updatePassword(authResult.user, password);
            return { success: true, userId: authResult.user.uid };
          } catch (error) {
            return { success: false, error: (error as Error).message };
          }
        })
        .catch((error: Error) => {
          return { success: false, error: error.message };
        });
    });
  }

  async getUserByEmail(orgId: string, email: string): Promise<User | null> {
    return safeExecute(async () => {
      const usersRef = collection(db, this.organizationCollection, orgId, this.usersCollection);
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return null;
      const res = getDocData(querySnapshot.docs[0]);
      return new User(res);
    });
  }

  async getOrganizationUsersByIds(
    organizationId: string,
    userIds: string[],
  ): Promise<
    {
      id: string;
      displayName?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      idCard?: string;
      photoURL?: string;
      address?: { country?: string };
    }[]
  > {
    return safeExecute(async () => {
      const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
      if (!organizationId || uniqueIds.length === 0) {
        return [];
      }

      const usersCollection = collection(
        db,
        this.organizationCollection,
        organizationId,
        this.usersCollection,
      );
      const profiles: {
        id: string;
        displayName?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        idCard?: string;
        photoURL?: string;
        address?: { country?: string };
      }[] = [];

      for (const idChunk of chunkIds(uniqueIds, FIRESTORE_IN_LIMIT)) {
        const snapshot = await getDocs(
          query(usersCollection, where(documentId(), 'in', idChunk)),
        );
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data() as {
            id?: string;
            displayName?: string;
            firstName?: string;
            lastName?: string;
            email?: string;
            idCard?: string;
            photoURL?: string;
            address?: { country?: string };
          };
          profiles.push({
            id: data.id || docSnap.id,
            displayName: data.displayName,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            idCard: data.idCard,
            photoURL: data.photoURL,
            address: data.address ? { country: data.address.country } : undefined,
          });
        });
      }

      return profiles;
    }) as Promise<
      {
        id: string;
        displayName?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        idCard?: string;
        photoURL?: string;
        address?: { country?: string };
      }[]
    >;
  }

  async getUser(orgId: string, userId: string): Promise<TUserOrganization | null> {
    return safeExecute(async () => {
      const userRef = doc(db, this.organizationCollection, orgId, this.usersCollection, userId);
      const userDoc = await getDoc(userRef);
      return getDocData(userDoc) as TUserOrganization | null;
    }) as Promise<TUserOrganization | null>;
  }

  async getNotificationTarget(
    orgId: string,
    userId: string,
  ): Promise<UserNotificationTargetDTO | null> {
    return safeExecute(async () => {
      const userRef = doc(db, this.organizationCollection, orgId, this.usersCollection, userId);
      const userDoc = await getDoc(userRef);
      return getDocData(userDoc) as UserNotificationTargetDTO | null;
    }) as Promise<UserNotificationTargetDTO | null>;
  }

  async changePassword(userId: string, newPassword: string): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const changePasswordFn = apiCallable<
        { userId: string; newPassword: string },
        ICloudFunctionResult
      >(API_OPERATIONS.CHANGE_PASSWORD);

      const { data } = await changePasswordFn({ userId, newPassword });

      if (!data?.success) {
        throw new Error(data?.error);
      }

      return { success: true };
    }).catch((error: Error) => {
      throw error;
    });
  }

  async changeUserEmail(userId: string, newEmail: string): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const changeEmailFn = apiCallable<
        { newEmail: string; process: string; userId: string },
        ICloudFunctionResult
      >(API_OPERATIONS.CHANGE_EMAIL);

      const { data } = await changeEmailFn({ newEmail, process: 'REQUEST', userId });
      return data;
    });
  }

  async confirmEmailChange(token: string): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const confirmChangeFn = apiCallable<{ token: string }, ICloudFunctionResult>(
        API_OPERATIONS.CONFIRM_EMAIL_CHANGE,
      );

      const { data } = await confirmChangeFn({ token });
      return data;
    });
  }

  async resendChangeEmail(userId: string): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const resendEmailFn = apiCallable<
        { process: string; userId: string },
        ICloudFunctionResult
      >(API_OPERATIONS.CHANGE_EMAIL);

      const { data } = await resendEmailFn({ process: 'RESEND', userId });
      return data;
    });
  }

  async sendEmailByUpdatePassword(userId: string): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const sendEmailByUpdatePasswordFn = apiCallable<{ userId: string }, ICloudFunctionResult>(
        API_OPERATIONS.SEND_ACCESS_KEY_MAIL,
      );

      const response = await sendEmailByUpdatePasswordFn({ userId });
      return response.data;
    });
  }

  async listElastic(params: ElasticQueryParams): Promise<ElasticSearchResponse | null> {
    return safeExecute(async () => {
      const genericFindFn = apiCallable<ElasticQueryParams, ElasticSearchResponse>(
        API_OPERATIONS.QUERY,
      );

      const response = await genericFindFn(params);
      const responseData = response.data;
      if (!responseData?.hits) return null;
      return responseData;
    });
  }

  async updateUserPermissions(
    orgId: string,
    params: Record<string, unknown>,
  ): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const updateUserPermissionsFn = apiCallable<Record<string, unknown>, ICloudFunctionResult>(
        API_OPERATIONS.UPDATE_USER_PERMISSIONS,
      );

      const { data } = await updateUserPermissionsFn(params);
      return data;
    });
  }

  async confirmPassword(currentPassword: string) {
    return safeExecute(async () => {
      const user = this.authUser();
  
      const credential = EmailAuthProvider.credential(
        user.currentUser.email,
        currentPassword
      );
  
      return this.reauthenticateWithCredential(user.currentUser, credential)
        .then(authResult => {
          return { success: true, userId: authResult.user.uid };
        })
        .catch(error => {
          const authErrorCode = error.code || null;
          /** English fallback when UI does not translate (e.g. tests, logs). */
          let legacyMessage: string;

          switch (authErrorCode) {
            case FIREBASE_AUTH_ERROR_CODES.WRONG_PASSWORD:
              legacyMessage = 'Incorrect password';
              break;
            case FIREBASE_AUTH_ERROR_CODES.TOO_MANY_REQUESTS:
              legacyMessage = 'Too many attempts. Try again later';
              break;
            case FIREBASE_AUTH_ERROR_CODES.USER_MISMATCH:
              legacyMessage = 'User mismatch';
              break;
            case FIREBASE_AUTH_ERROR_CODES.USER_NOT_FOUND:
              legacyMessage = 'User not found';
              break;
            case FIREBASE_AUTH_ERROR_CODES.NETWORK_REQUEST_FAILED:
              legacyMessage = 'Network error. Check your connection';
              break;
            default:
              legacyMessage = 'Failed to validate password';
              break;
          }

          return {
            success: false,
            error: legacyMessage,
            authErrorCode,
          };
        });
    });
  }

  async getUsersWithJourney(
    params: IGetUsersWithJourneyParams,
  ): Promise<IGetUsersWithJourneyResult> {
    return safeExecute(async () => {
      const getUsersWithJourneyFn = apiCallable<
        IGetUsersWithJourneyParams,
        IGetUsersWithJourneyResult
      >(API_OPERATIONS.GET_USERS_WITH_JOURNEY);

      const { organizationId, pageSize, pageToken, workShiftId, workJourneyConfigId, searchTerm, searchFields } = params;
      const payload: IGetUsersWithJourneyParams = { organizationId };
      if (pageSize != null) payload.pageSize = pageSize;
      if (pageToken != null) payload.pageToken = pageToken;
      if (workShiftId != null) payload.workShiftId = workShiftId;
      if (workJourneyConfigId != null) payload.workJourneyConfigId = workJourneyConfigId;
      if (searchTerm != null && searchTerm.trim() !== '') payload.searchTerm = searchTerm.trim();
      if (searchFields != null && searchFields.length > 0) payload.searchFields = searchFields;

      const response = await getUsersWithJourneyFn(payload);
      return response.data;
    });
  }

  async getGeneralUserInfo(organizationId: string, userId: string): Promise<ICloudFunctionResult> {
    return safeExecute(async () => {
      const userInfoFn = apiCallable<
        { organizationId: string; userId: string },
        ICloudFunctionResult
      >(API_OPERATIONS.GET_USER_INFO);

      const { data } = await userInfoFn({ organizationId, userId });
      return data;
    });
  }

  async getUserInfoSingleton(userId: string): Promise<unknown | null> {
    return safeExecute(async () => {
      if (!userId) {
        return null;
      }

      const USER_INFO_SINGLETON_DOC_ID = 'UNIQUE_ID';
      const infoRef = doc(
        db,
        FSCollections.USERS,
        userId,
        FSCollections.USER_INFO,
        USER_INFO_SINGLETON_DOC_ID,
      );
      const infoSnap = await getDoc(infoRef);

      if (!infoSnap.exists()) {
        return null;
      }

      return infoSnap.data();
    });
  }
}

export default UserRepository;
